import React, { useState, useEffect } from 'react';
import { maintenanceAPI, deviceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const Maintenances = () => {
  const { isAdmin, isIT } = useAuth();
  const [maintenances, setMaintenances] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingMaintenance, setEditingMaintenance] = useState(null);
  const [devices, setDevices] = useState([]);
  const [formData, setFormData] = useState({
    deviceId: '',
    maintenanceType: 'preventive',
    scheduledDate: '',
    description: '',
    notes: '',
    cost: ''
  });

  useEffect(() => {
    fetchMaintenances();
    fetchStats();
    fetchDevices();
  }, []);

  const fetchMaintenances = async () => {
    try {
      const response = await maintenanceAPI.getAll();
      setMaintenances(response.data);
    } catch (error) {
      console.error('Failed to fetch maintenances:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await maintenanceAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await deviceAPI.getAll();
      setDevices(response.data.devices || response.data);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMaintenance) {
        await maintenanceAPI.update(editingMaintenance.id, formData);
      } else {
        await maintenanceAPI.create(formData);
      }
      setShowModal(false);
      setEditingMaintenance(null);
      resetForm();
      fetchMaintenances();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Bir hata oluştu');
    }
  };

  const handleEdit = (maintenance) => {
    setEditingMaintenance(maintenance);
    setFormData({
      deviceId: maintenance.deviceId,
      maintenanceType: maintenance.maintenanceType,
      scheduledDate: maintenance.scheduledDate ? maintenance.scheduledDate.split('T')[0] : '',
      description: maintenance.description || '',
      notes: maintenance.notes || '',
      cost: maintenance.cost || ''
    });
    setShowModal(true);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'completed') {
        updateData.completedDate = new Date().toISOString().split('T')[0];
      }
      await maintenanceAPI.update(id, updateData);
      fetchMaintenances();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Güncelleme hatası');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu bakım kaydını silmek istediğinize emin misiniz?')) {
      try {
        await maintenanceAPI.delete(id);
        fetchMaintenances();
        fetchStats();
      } catch (error) {
        alert(error.response?.data?.error || 'Silme hatası');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      deviceId: '',
      maintenanceType: 'preventive',
      scheduledDate: '',
      description: '',
      notes: '',
      cost: ''
    });
  };

  const filteredMaintenances = maintenances.filter(m => {
    const matchesSearch = m.device?.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.description?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !filterStatus || m.status === filterStatus;
    const matchesType = !filterType || m.maintenanceType === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('tr-TR');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
  };

  const maintenanceTypes = {
    preventive: { label: 'Önleyici', color: 'bg-blue-100 text-blue-700' },
    corrective: { label: 'Düzeltici', color: 'bg-orange-100 text-orange-700' },
    upgrade: { label: 'Yükseltme', color: 'bg-purple-100 text-purple-700' },
    inspection: { label: 'Denetim', color: 'bg-cyan-100 text-cyan-700' },
    cleaning: { label: 'Temizlik', color: 'bg-green-100 text-green-700' }
  };

  const statusTypes = {
    scheduled: { label: 'Planlı', color: 'bg-yellow-100 text-yellow-700', icon: CalendarDaysIcon },
    in_progress: { label: 'Devam Ediyor', color: 'bg-blue-100 text-blue-700', icon: ClockIcon },
    completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700', icon: CheckCircleIcon },
    cancelled: { label: 'İptal', color: 'bg-gray-100 text-gray-700', icon: XMarkIcon }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bakım Takibi</h1>
          <p className="text-gray-600">Cihaz bakımlarını planlayıp takip edin</p>
        </div>
        <button
          onClick={() => { setShowModal(true); setEditingMaintenance(null); resetForm(); }}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Yeni Bakım
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <WrenchScrewdriverIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Toplam Bakım</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <CalendarDaysIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Planlı</p>
                <p className="text-xl font-bold">{stats.scheduled}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <ClockIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Devam Eden</p>
                <p className="text-xl font-bold">{stats.inProgress}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Tamamlanan</p>
                <p className="text-xl font-bold">{stats.completed}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Cihaz veya açıklama ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tüm Durumlar</option>
            {Object.entries(statusTypes).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tüm Türler</option>
            {Object.entries(maintenanceTypes).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Maintenance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredMaintenances.map((maintenance) => {
          const typeInfo = maintenanceTypes[maintenance.maintenanceType] || { label: maintenance.maintenanceType, color: 'bg-gray-100 text-gray-700' };
          const statusInfo = statusTypes[maintenance.status] || statusTypes.scheduled;
          const StatusIcon = statusInfo.icon;

          return (
            <div key={maintenance.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="p-4 border-b">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900">{maintenance.device?.name || 'Cihaz'}</h3>
                    <p className="text-sm text-gray-500">{maintenance.device?.serialNumber}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${typeInfo.color}`}>
                    {typeInfo.label}
                  </span>
                  <span className="text-sm text-gray-500">
                    {formatDate(maintenance.scheduledDate)}
                  </span>
                </div>

                {maintenance.description && (
                  <p className="text-sm text-gray-600">{maintenance.description}</p>
                )}

                {maintenance.cost && (
                  <div className="text-sm">
                    <span className="text-gray-500">Maliyet: </span>
                    <span className="font-medium">{formatCurrency(maintenance.cost)}</span>
                  </div>
                )}

                {maintenance.performedBy && (
                  <div className="text-sm">
                    <span className="text-gray-500">Yapan: </span>
                    <span>{maintenance.performer?.fullName || '-'}</span>
                  </div>
                )}
              </div>
              <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
                <div className="flex space-x-2">
                  {maintenance.status === 'scheduled' && (
                    <button
                      onClick={() => handleStatusChange(maintenance.id, 'in_progress')}
                      className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      Başla
                    </button>
                  )}
                  {maintenance.status === 'in_progress' && (
                    <button
                      onClick={() => handleStatusChange(maintenance.id, 'completed')}
                      className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                    >
                      Tamamla
                    </button>
                  )}
                  {(maintenance.status === 'scheduled' || maintenance.status === 'in_progress') && (
                    <button
                      onClick={() => handleStatusChange(maintenance.id, 'cancelled')}
                      className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      İptal
                    </button>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(maintenance)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(maintenance.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredMaintenances.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <WrenchScrewdriverIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Bakım kaydı bulunamadı</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">
                {editingMaintenance ? 'Bakımı Düzenle' : 'Yeni Bakım'}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <XMarkIcon className="h-6 w-6 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cihaz *</label>
                <select
                  value={formData.deviceId}
                  onChange={(e) => setFormData({ ...formData, deviceId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Cihaz Seçin</option>
                  {devices.map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.name} ({device.serialNumber})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bakım Türü</label>
                <select
                  value={formData.maintenanceType}
                  onChange={(e) => setFormData({ ...formData, maintenanceType: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {Object.entries(maintenanceTypes).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Planlanan Tarih *</label>
                <input
                  type="date"
                  value={formData.scheduledDate}
                  onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Maliyet (TL)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  {editingMaintenance ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Maintenances;
